import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import cx from 'clsx';

import {
	ChartAxisType,
	ChartOrientation,
	ChartSeriesOrientation,
	ChartType
} from '@soomo/lib/types/index';
import { type ChartElement as ManifestChartElement } from '@soomo/lib/types/WebtextManifest';
import { Figure } from '@soomo/lib/components/pageElements/index';
import RefreshedChart from '@soomo/lib/components/pageElements/ChartFigure/RefreshedChart/Chart';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { chartElement } from './styles';
import LoadDataModal from './LoadDataModal';
import { chartToChartProperties } from '@editor2/mappers/element';
import CopyInput from '@editor2/components/CopyInputWrapper';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import LineChartFields from './LineChartFields';
import ExportTSV from './ExportTSV';
import ChartDataTableWithPagination from './ChartDataTableWrapper';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import ChartFromLibs from '@soomo/lib/components/pageElements/ChartFigure/Chart';
import { type RootState } from '@editor2/reducers/root';
import { elementCommonStyles } from '@editor2/styles/element';
import { type Chart as ChartElementType } from '@editor2/types/pageElements';

interface Props {
	element: ChartElementType;
	courseId: number;
	pageType: string;
}

const ChartElement: FC<Props> = (props) => {
	const { element, courseId, pageType } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const [isLoadDataModalShown, setLoadDataModalShown] = useState(false);
	const [chartProperties, handleLocalChange] = useDebounceProtectedState(
		element,
		chartToChartProperties
	);

	const { page_types } = useSelector((state: RootState) => state.courses[courseId].config);

	const tabsUIEnabled = page_types[pageType].elements['Chart']?.settings?.tabs_ui;

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const handleChange = (value) => {
		handleLocalChange(value);
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleDataLoadSubmit = (data: any[]) => {
		const isColorsRow = data[0][1].startsWith('#');

		let chartData;
		let chartColors;

		if (isColorsRow) {
			chartColors = data[0].slice(1);
			chartData = data.slice(1);
		} else {
			chartColors = [...Array(data[0].length - 1)].map(
				(_, index) => element.colors[index] || '#123456'
			);
			chartData = data;
		}

		dispatch(updateElement({ ...commonPayload, data: chartData, colors: chartColors }));
	};

	const marginsOrder = ['top', 'right', 'bottom', 'left'];

	return (
		<Form className={cx(chartElement, elementCommonStyles)}>
			<CopyInput label="Family ID" value={chartProperties.family_id} />

			<Form.Group>
				<Form.Label>Figure number</Form.Label>
				<Form.Control
					value={chartProperties.figure_number}
					placeholder="Figure number"
					size="sm"
					className="figureNumber"
					onChange={(e) => handleChange({ figure_number: e.target.value })}
				/>
			</Form.Group>
			<Form.Group>
				<Form.Label>Title</Form.Label>
				<Form.Control
					value={chartProperties.title}
					placeholder="Title"
					size="lg"
					className="title"
					onChange={(e) => handleChange({ title: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Preview</Form.Label>
				{tabsUIEnabled ? (
					<RefreshedChart chart={element as unknown as ManifestChartElement} />
				) : (
					<div className="chartPreviewWrapper">
						<Figure
							figureOptions={{
								title: element.title,
								payload: <ChartFromLibs chart={element as unknown as ManifestChartElement} />,
								caption: element.caption,
								credits: element.credit,
								description: element.description,
								figure_number: element.figure_number
							}}
						/>
					</div>
				)}
			</Form.Group>

			<Form.Group className="tableButtonsBlock">
				<div>
					<Form.Label>Load data</Form.Label>
					<Button
						variant="outline-secondary"
						size="sm"
						onClick={() => setLoadDataModalShown(true)}
						className="loadButton">
						Choose ...
					</Button>
				</div>

				<ExportTSV data={[['', ...element.colors], ...element.data]} />
			</Form.Group>

			<Form.Group className="dataTableContainer">
				<ChartDataTableWithPagination
					element={element}
					courseId={courseId}
					key={element.family_id}
				/>
			</Form.Group>

			<div className="block">
				<Form.Group>
					<Form.Label>Type</Form.Label>
					<Form.Control
						as="select"
						className="select"
						value={chartProperties.chart_type}
						onChange={(e) =>
							handleChange({
								chart_type: e.target.value,
								orientation: e.target.value === 'bar' ? 'horizontal' : undefined,
								series_orientation:
									e.target.value === 'bar' && chartProperties.data[0]?.length
										? 'grouped'
										: undefined
							})
						}>
						{Object.keys(ChartType).map((type) => (
							<option key={type} value={type}>
								{type}
							</option>
						))}
					</Form.Control>
				</Form.Group>

				{chartProperties.chart_type === ChartType.bar && (
					<>
						<Form.Group>
							<Form.Label>Orientation</Form.Label>
							<Form.Control
								as="select"
								className="select"
								value={chartProperties.orientation}
								onChange={(e) =>
									handleChange({
										orientation: e.target.value,
										x_axis_label: chartProperties.y_axis_label,
										y_axis_label: chartProperties.x_axis_label
									})
								}>
								{Object.keys(ChartOrientation).map((type) => (
									<option key={type} value={type}>
										{type}
									</option>
								))}
							</Form.Control>
						</Form.Group>

						{chartProperties.data[0]?.length > 2 && (
							<Form.Group>
								<Form.Label>Presentation</Form.Label>
								<Form.Control
									as="select"
									className="select"
									value={chartProperties.series_orientation}
									onChange={(e) => handleChange({ series_orientation: e.target.value })}>
									{Object.keys(ChartSeriesOrientation).map((type) => (
										<option key={type} value={type}>
											{type}
										</option>
									))}
								</Form.Control>
							</Form.Group>
						)}
					</>
				)}
			</div>
			{chartProperties.chart_type !== ChartType.pie && (
				<Form.Group className="block">
					<Form.Label>Value axis</Form.Label>

					<Form.Group>
						<Form.Control
							type="number"
							step={1}
							value={chartProperties.min_bound}
							className="number"
							onChange={(e) => handleChange({ min_bound: Number(e.target.value) })}
						/>
						<Form.Text className="text-muted">Min bound</Form.Text>
					</Form.Group>

					<Form.Group>
						<Form.Control
							type="number"
							step={1}
							value={chartProperties.max_bound}
							className="number"
							onChange={(e) => handleChange({ max_bound: Number(e.target.value) })}
						/>
						<Form.Text className="text-muted">Max bound</Form.Text>
					</Form.Group>

					{tabsUIEnabled && (
						<Form.Group>
							<Form.Control
								type="number"
								min={0}
								step={1}
								value={chartProperties.round_to}
								className="number"
								onChange={(e) => {
									const value = Number(e.target.value);
									handleChange({
										round_to: value < 0 ? 0 : value
									});
								}}
							/>
							<Form.Text className="text-muted">Round to decimal</Form.Text>
						</Form.Group>
					)}
				</Form.Group>
			)}

			{chartProperties.chart_type === ChartType.line && (
				<LineChartFields
					chartProperties={chartProperties}
					onChange={handleChange}
					tabsUIEnabled={tabsUIEnabled}
				/>
			)}

			{tabsUIEnabled &&
				chartProperties.chart_type === ChartType.bar &&
				chartProperties.orientation === 'horizontal' && (
					<div className="block">
						<Form.Group>
							<Form.Label>Category labels width in px</Form.Label>
							<Form.Control
								type="number"
								step={1}
								value={chartProperties.labels_width}
								className="number"
								onChange={(e) => handleChange({ labels_width: Number(e.target.value) })}
							/>
						</Form.Group>
					</div>
				)}

			{chartProperties.chart_type === ChartType.bar && (
				<div className="block">
					<Form.Group>
						<Form.Check
							type="checkbox"
							checked={!!chartProperties.show_labels}
							onChange={() => handleChange({ show_labels: !chartProperties.show_labels })}
							label="Show data labels"
						/>
					</Form.Group>
				</div>
			)}

			{!tabsUIEnabled && (
				<div className="block">
					<Form.Group>
						<Form.Label>Categories axis type</Form.Label>
						<Form.Control
							as="select"
							className="select"
							value={chartProperties.axis_type}
							onChange={(e) => handleChange({ axis_type: e.target.value })}>
							{Object.keys(ChartAxisType).map((type) => (
								<option key={type} value={type}>
									{type}
								</option>
							))}
						</Form.Control>
					</Form.Group>
				</div>
			)}

			{chartProperties.chart_type === ChartType.bar && (
				<div className="block">
					<Form.Group>
						<Form.Label>Category axis title</Form.Label>
						<Form.Control
							value={
								chartProperties.orientation === 'horizontal'
									? chartProperties.y_axis_label
									: chartProperties.x_axis_label
							}
							placeholder="Category axis title"
							onChange={(e) =>
								handleChange({
									[chartProperties.orientation === 'horizontal' ? 'y_axis_label' : 'x_axis_label']:
										e.target.value
								})
							}
						/>
					</Form.Group>

					<Form.Group>
						<Form.Label>Value axis title</Form.Label>
						<Form.Control
							value={
								chartProperties.orientation === 'horizontal'
									? chartProperties.x_axis_label
									: chartProperties.y_axis_label
							}
							placeholder="Value axis title"
							onChange={(e) =>
								handleChange({
									[chartProperties.orientation === 'horizontal' ? 'x_axis_label' : 'y_axis_label']:
										e.target.value
								})
							}
						/>
					</Form.Group>
				</div>
			)}

			{!tabsUIEnabled && (
				<Form.Group className="block">
					<Form.Label>Margin adjustments (in pixels)</Form.Label>

					<div className="marginsBlock">
						{chartProperties.margins &&
							chartProperties.margins.map((margin, i) => (
								<Form.Group key={i}>
									<Form.Control
										type="number"
										step={1}
										value={margin || 0}
										className="number"
										onChange={(e) =>
											handleChange({
												margins: [
													...chartProperties.margins.slice(0, i),
													Number(e.target.value),
													...chartProperties.margins.slice(i + 1)
												]
											})
										}
									/>
									<Form.Text className="text-muted">{marginsOrder[i]}</Form.Text>
								</Form.Group>
							))}
					</div>
				</Form.Group>
			)}

			<Form.Group>
				<Form.Label>Description</Form.Label>
				<CodeMirrorHTML
					value={chartProperties.description}
					showTips={false}
					options={{ placeholder: 'Description (for accessibility)' }}
					onChange={(value) => handleChange({ description: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Caption</Form.Label>
				<CodeMirrorHTML
					value={chartProperties.caption}
					showTips={false}
					options={{ placeholder: 'Caption' }}
					onChange={(value) => handleChange({ caption: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Credits</Form.Label>
				<CodeMirrorHTML
					value={chartProperties.credit}
					showTips={false}
					options={{ placeholder: 'Credit' }}
					onChange={(value) => handleChange({ credit: value })}
				/>
			</Form.Group>

			{isLoadDataModalShown && (
				<LoadDataModal
					onHide={() => setLoadDataModalShown(false)}
					onSubmit={handleDataLoadSubmit}
					isShown={isLoadDataModalShown}
				/>
			)}
		</Form>
	);
};

export default ChartElement;
